import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Star, ImagePlus, Loader2, X } from "lucide-react";
import api, { resolveImg, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Stars from "./Stars";

export default function Reviews({ productId, onChange }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get(`/products/${productId}/reviews`).then((r) => setReviews(r.data));
  };

  useEffect(() => {
    load();
    setName(user?.name || "");
    setRating(0);
    setComment("");
    setPhoto("");
    setEmail("");
    // eslint-disable-next-line
  }, [productId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload/review", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhoto(resolveImg(data.url));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not upload photo");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please choose a star rating");
    if (!name.trim()) return toast.error("Please enter your name");
    if (!user && !email.trim()) return toast.error("Please enter the email you used for your order");
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, {
        name,
        rating,
        comment,
        photo_url: photo,
        email: user ? undefined : email.trim(),
      });
      toast.success("Thank you for your review!");
      setRating(0);
      setComment("");
      setPhoto("");
      setEmail("");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="mt-24" data-testid="reviews-section">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Summary + form */}
        <div className="lg:col-span-1">
          <h2 className="text-2xl sm:text-3xl text-primary font-medium">Reviews</h2>
          <div className="mt-4 flex items-center gap-3">
            <span className="font-display text-4xl text-primary">{avg || "—"}</span>
            <div>
              <Stars value={Number(avg)} size={16} />
              <p className="text-xs text-muted-foreground mt-1">
                {reviews.length} review{reviews.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 border border-border p-5 bg-secondary/30" data-testid="review-form">
            <p className="text-sm font-semibold text-foreground mb-1">Write a review</p>
            <p className="text-xs text-muted-foreground mb-3">
              Only verified buyers can review this product.
            </p>
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(i)}
                  data-testid={`review-star-${i}`}
                >
                  <Star
                    size={26}
                    className={i <= (hover || rating) ? "text-accent fill-accent" : "text-border"}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full bg-muted px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary mb-3"
              data-testid="review-name"
            />
            {!user && (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email used on your order"
                className="w-full bg-muted px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary mb-3"
                data-testid="review-email"
              />
            )}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your thoughts…"
              rows={3}
              className="w-full bg-muted px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary mb-3 resize-none"
              data-testid="review-comment"
            />
            {photo ? (
              <div className="relative w-20 h-20 mb-3">
                <img src={photo} alt="upload" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setPhoto("")}
                  className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full p-1"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                data-testid="review-add-photo"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                {uploading ? "Uploading…" : "Add a photo (optional)"}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-primary-foreground py-3 text-sm tracking-wide flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="review-submit"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />} Submit Review
            </button>
          </form>
        </div>

        {/* List */}
        <div className="lg:col-span-2">
          {reviews.length === 0 ? (
            <p className="text-muted-foreground py-8" data-testid="reviews-empty">
              No reviews yet — be the first to share your experience.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {reviews.map((r) => (
                <div key={r.id} className="py-6" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                        {r.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {r.name}
                          {r.verified && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5">
                              Verified
                            </span>
                          )}
                        </p>
                        <Stars value={r.rating} size={12} />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  {r.comment && <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{r.comment}</p>}
                  {r.photo_url && (
                    <img
                      src={resolveImg(r.photo_url)}
                      alt="review"
                      className="mt-3 w-28 h-28 object-cover border border-border"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
